import { useEffect, useState } from "react";
import { navLinks } from "@/mocks/home";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [active, setActive] = useState("scenes");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const sections = navLinks
      .map((link) => document.getElementById(link.id))
      .filter((el): el is HTMLElement => Boolean(el));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  const handleJump = (id: string) => {
    setMenuOpen(false);
    const target = document.getElementById(id);
    if (target) {
      const top = target.getBoundingClientRect().top + window.scrollY - 68;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-background-50/95 backdrop-blur border-b border-background-200" : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center gap-2 cursor-pointer"
            aria-label="神马电影网 返回顶部"
          >
            <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-film-line text-lg"></i>
            </span>
            <span
              className={`font-heading text-lg font-bold tracking-wide transition-colors ${
                scrolled ? "text-foreground-950" : "text-foreground-950 drop-shadow-sm"
              }`}
            >
              神马电影网
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-1" aria-label="页面分区导航">
            {navLinks.map((link) => (
              <button
                key={link.id}
                type="button"
                onClick={() => handleJump(link.id)}
                className={`px-3 py-2 text-sm rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                  active === link.id
                    ? "text-primary-600 font-medium bg-primary-100/70"
                    : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                }`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            <button
              type="button"
              onClick={() => handleJump("browse")}
              className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-circle-line text-base"></i>
              开始观看
            </button>
          </div>

          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-900 hover:bg-background-100 cursor-pointer"
            aria-label="展开导航菜单"
            aria-expanded={menuOpen}
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden bg-background-50 border-b border-background-200 animate-soft-fade">
          <nav className="px-4 py-3 grid grid-cols-2 gap-2" aria-label="移动端分区导航">
            {navLinks.map((link) => (
              <button
                key={link.id}
                type="button"
                onClick={() => handleJump(link.id)}
                className={`px-3 py-2.5 text-sm text-left rounded-md transition-colors cursor-pointer ${
                  active === link.id
                    ? "text-primary-600 font-medium bg-primary-100/70"
                    : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                {link.label}
              </button>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}